import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Seo } from "../components/Seo";
import { CoachPick } from "../components/CoachPick";

const SITE = process.env.REACT_APP_BACKEND_URL || "";

export default function Landing() {
  const { user } = useAuth();
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Dr. Cuts",
    "description": "Members-only golf coaching studio specializing in the cut shot, with online swing feedback and in-person Trackman lessons.",
    "url": SITE,
    "image": "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?auto=format&fit=crop&w=1200&q=80",
    "priceRange": "$10 - $50",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Waynesville",
      "addressRegion": "NC",
      "postalCode": "28786",
      "addressCountry": "US"
    },
    "knowsAbout": "Golf cut shot coaching",
    "makesOffer": [
      { "@type": "Offer", "name": "Cut Hunter Membership", "price": "10.00", "priceCurrency": "USD" },
      { "@type": "Offer", "name": "In-Person Trackman Lesson", "price": "50.00", "priceCurrency": "USD" }
    ]
  };
  return (
    <div data-testid="landing-page" className="relative">
      <Seo
        title=""
        description="Dr. Cuts is a members-only golf studio for mastering the high-holding fade. Upload your swing, get frame-by-frame coach teardowns, and book in-person Trackman lessons."
        path="/"
        jsonLd={jsonLd}
      />
      {/* Hero */}
      <section className="relative min-h-[100vh] overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1698330442250-81861beba7ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxnb2xmJTIwY291cnNlJTIwbW9ybmluZyUyMGZvZ3xlbnwwfHx8fDE3ODIyNDkxNDd8MA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover", backgroundPosition: "center",
          maskImage: "linear-gradient(to bottom, black 30%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 30%, transparent 100%)",
        }} />
        <div className="absolute inset-x-0 bottom-0 h-[60%] pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #050A08 80%)" }} />

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 pt-40 md:pt-48 pb-20">
          <div className="max-w-3xl">
            <div className="overline fade-up">A coaching laboratory for the cut shot</div>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-[88px] leading-[0.95] font-semibold mt-6 fade-up delay-1">
              Dr.
              <span className="italic text-[#C9A227]"> Cuts</span>
              <span className="block text-white/70 font-serif italic text-3xl sm:text-4xl mt-3">left to right, on command.</span>
            </h1>
            <p className="mt-8 max-w-xl text-white/70 text-lg fade-up delay-2">
              A members-only studio for players obsessed with the high, holding fade.
              Upload your swing. Get personal teardown notes from your coach. Repeat until the ball bends exactly where you want it.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4 fade-up delay-3">
              {user ? (
                <Link to="/feed" data-testid="hero-cta-feed" className="btn-primary">Enter the Studio</Link>
              ) : (
                <Link to="/register" data-testid="hero-cta-start" className="btn-primary">Create Account</Link>
              )}
              <Link to="/pricing" data-testid="hero-cta-pricing" className="btn-ghost">Membership · $10/mo</Link>
              <Link to="/lesson" data-testid="hero-cta-lesson" className="btn-ghost">In-Person Lessons · $50</Link>
            </div>
          </div>
        </div>
      </section>

      <CoachPick />

      {/* Pillars */}
      <section className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <span className="overline">The Method</span>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-[1]">Three pieces.<br/>One shot shape.</h2>
            <span className="divider-gold mt-8" />
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-3 gap-6">
            {[
              { n:"01", t:"Path", b:"Out-to-in but not over-cooked. We measure your swing direction in real footage." },
              { n:"02", t:"Face", b:"Face open to path, closed to target. The geometry that bends a ball right." },
              { n:"03", t:"Strike", b:"Center-face contact under pressure. No fade lives without it." },
            ].map((p) => (
              <div key={p.n} className="p-6 border border-white/10 bg-white/[0.02]" data-testid={`pillar-${p.n}`}>
                <div className="overline">{p.n}</div>
                <h3 className="font-display text-3xl mt-3">{p.t}</h3>
                <p className="mt-3 text-white/65 text-sm leading-relaxed">{p.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="relative bg-[#08100D] border-y border-white/5 py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <span className="overline">How it works</span>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-[1]">From your driveway to a real feedback loop.</h2>
            <p className="text-white/65 mt-6 leading-relaxed">No vague tips. No mass-produced courses. You film, we respond — by name, by frame, by shot.</p>
          </div>
          <div className="md:col-span-7 space-y-6">
            {[
              ["Become a member", "Lock in your monthly seat for $10. That unlocks the upload bay."],
              ["Upload a swing", "Drop a clip from your phone. Down-the-line or face-on, doesn't matter."],
              ["Get a teardown", "Your coach writes back inside the comment thread — pinned, highlighted, gold."],
              ["Iterate", "Re-upload until that ball curves the way you've been chasing for years."],
            ].map((s, i) => (
              <div key={s[0]} className="flex gap-6 items-start" data-testid={`step-${i+1}`}>
                <div className="font-serif text-5xl text-[#C9A227] leading-none w-14">{(i+1).toString().padStart(2,"0")}</div>
                <div>
                  <h4 className="font-display text-2xl">{s[0]}</h4>
                  <p className="text-white/60 mt-1 text-sm">{s[1]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <span className="overline">Tee it up</span>
          <h2 className="font-display text-5xl sm:text-7xl mt-4 leading-[0.95]">Stop hoping. <span className="italic text-[#C9A227]">Start shaping.</span></h2>
          <p className="text-white/65 mt-6 max-w-xl mx-auto">Ten dollars a month. 3 swing uploads. Direct, written coaching on every clip, and a path to hitting a fade successfully.</p>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <Link to={user ? "/pricing" : "/register"} data-testid="cta-bottom" className="btn-primary">{user ? "Go Pro" : "Create Account"}</Link>
            <Link to="/feed" data-testid="cta-bottom-feed" className="btn-ghost">Peek the Feed</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
